import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { Box } from '@/components/Box/Box';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { Button } from '@/components/Button/Button';
import PlusIcon from '@/images/PlusIcon.svg?react';
import { OrganizationUserTable } from '@/bundle/_MasterAdmin/ui/OrganizationUserTable/OrganizationUserTable';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { Drawer } from '@/components/Drawer/Drawer';
import { useState } from 'react';
import { OrganizationDetailsPanel } from '@/bundle/_MasterAdmin/ui/OrganizationDetailsPanel/OrganizationDetailsPanel';
import { CreateAdminForm } from '@/bundle/_MasterAdmin/ui/CreateAdminForm/CreateAdminForm';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { getInternalOrganizationDetails, getInternalOrganizationUsers } from '@/bundle/_MasterAdmin/api/api';
import { useUser } from '@/context/userContext';
import { getUsersMaxLimit, isStandardPlanType, isUsersLimitReached } from '@/helpers/billingHelpers';
import { UserLimitReachedModal } from '@/bundle/shared/components/UserLimitReachedModal/UserLimitReachedModal';
import { useSubscriptionStatus } from '../shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '../shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { RoundedButton } from '@/components/RoundedButton/RoundedButton';
import { useNavigate } from 'react-router';
import { getMasterAdminMasterServiceAgreementPath } from './_MasterServiceAgreementPage/path/path';
import RightArrowIcon from '@/images/RightArrowIcon.svg?react';
import { getListData } from '@/helpers/helpers';
import { isAcceptedMasterServiceAgreement } from '@/helpers/userHelpers';

export const OrganizationDetailsPage = () => {
  const navigate = useNavigate();
  const searchParams = useGetSearchParams();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { isCancelledSubscription, isTrialAdmin } = useSubscriptionStatus();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openUsersLimitModal, setOpenUsersLimitModal] = useState(false);
  const isStandardPlan = isStandardPlanType(user);

  const closeCreateUserDrawer = () => setOpenDrawer(false);

  const openCreateUserDrawer = () => {
    const { active_users_count } = organizationDetails;
    const maxUsersLimit = getUsersMaxLimit(user);

    if (isStandardPlan && isUsersLimitReached(active_users_count, maxUsersLimit)) {
      setOpenUsersLimitModal(true);

      return;
    }

    setOpenDrawer(true);
  };

  const { data: organizationDetailsData } = useQuery({
    queryKey: ['get_internal_organization_details'],
    queryFn: () => getInternalOrganizationDetails(),
  });

  const { data: organizationUsersData, isPending: isPendingOrganizationUsers } = useQuery({
    queryKey: ['get_internal_organization_users', searchParams],
    queryFn: () => getInternalOrganizationUsers(searchParams),
    placeholderData: keepPreviousData,
  });

  const createAdminSuccess = () => {
    setOpenDrawer(false);
    queryClient.invalidateQueries({ queryKey: ['get_internal_organization_details'] });
    queryClient.invalidateQueries({ queryKey: ['get_internal_organization_users', searchParams] });
  };

  const navigateToMasterServiceAgreement = () => {
    navigate(getMasterAdminMasterServiceAgreementPath());
  };

  const organizationDetails = organizationDetailsData?.body;
  const { list, totalCount, hasPagination } = getListData(organizationUsersData?.body, searchParams);
  const isAcceptedAgreement = isAcceptedMasterServiceAgreement(user);
  const hasMSAButton = isStandardPlan && isAcceptedAgreement;
  const showSubscriptionStatusBanner = isCancelledSubscription || isTrialAdmin;

  return (
    <ContentLayout
      title='Organization details'
      breadcrumbs='Organization Details'
      banner={showSubscriptionStatusBanner && <OrganizationSubscriptionStatusBanner />}
      headerAction={
        hasMSAButton && (
          <RoundedButton onClick={navigateToMasterServiceAgreement} icon={<RightArrowIcon />}>
            View MSA
          </RoundedButton>
        )
      }
    >
      <PageBody>
        <Box display='flex' flexDirection='column' rowGap='58px'>
          <OrganizationDetailsPanel organizationDetails={organizationDetails} />
          <Box display='flex' flexDirection='column' rowGap='24px'>
            <Box display='flex' flexDirection='column' rowGap='8px'>
              <Box display='flex' width='100%' alignItems='center' justifyContent='space-between'>
                <Typography variant='headline4' color={COLORS.black} fontWeight='medium'>
                  Users
                </Typography>
                <Button
                  disabled={isCancelledSubscription}
                  width={143}
                  variant='primary'
                  size='medium'
                  iconPosition='left'
                  icon={<PlusIcon className={isCancelledSubscription ? 'disabled-svg-icon' : ''} />}
                  onClick={openCreateUserDrawer}
                >
                  Create User
                </Button>
              </Box>
              <Typography variant='bodySmall' color={COLORS.grey[700]}>
                View the list of users of your organization. Click on a particular row to view the data of the
                particular User.
              </Typography>
            </Box>
            <OrganizationUserTable items={list} isLoading={isPendingOrganizationUsers} />
            {hasPagination && (
              <PageFooter>
                <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
              </PageFooter>
            )}
          </Box>
        </Box>
      </PageBody>
      <Drawer
        isOpen={openDrawer}
        header='Create User'
        subHeader='Please provide information for the new user.'
        onClose={closeCreateUserDrawer}
      >
        <CreateAdminForm onCancel={closeCreateUserDrawer} onSuccess={createAdminSuccess} />
      </Drawer>
      <UserLimitReachedModal isOpen={openUsersLimitModal} onClose={() => setOpenUsersLimitModal(false)} />
    </ContentLayout>
  );
};
